import { useQuery, useMutation, useLazyQuery } from "@apollo/client";
import {
  GET_BUSINESS_VIA_IDENTIFIER,
  GET_SALON_FOR_BOOKING,
  GET_BUNDLES_FOR_BOOKING,
  GET_FAQS,
  SEARCH_CLIENT,
  CREATE_CLIENT,
  CREATE_APPOINTMENT,
  CREATE_APPOINTMENT_WITH_CLIENT_SECRET,
  GET_APPOINTMENT_SUMMARY,
  SALON_AVAILABILITY,
  GET_SERVICE_STAFFS,
  CREATE_GIFT_CARD_PURCHASE,
  CREATE_VOUCHER_PURCHASE,
  INITIATE_GIFT_CARD_PURCHASE,
  INITIATE_PRODUCT_PURCHASE,
  PRODUCT_PURCHASE_PREVIEW,
  PRODUCT_ORDER_SUMMARY,
  VOUCHER_PREVIEW,
} from "../queries/booking";

// Business and Salon queries
export const useGetBusinessViaIdentifier = (bookingUrlIdentifier: string) => {
  return useQuery(GET_BUSINESS_VIA_IDENTIFIER, {
    variables: { bookingUrlIdentifier },
    skip: !bookingUrlIdentifier,
  });
};

export const useGetSalonForBooking = (salonId: string | null) => {
  return useQuery(GET_SALON_FOR_BOOKING, {
    variables: { salonId },
    skip: !salonId,
  });
};

export const useGetBundlesForBooking = (salonId: string | null) => {
  return useQuery(GET_BUNDLES_FOR_BOOKING, {
    variables: { salonId, active: true },
    skip: !salonId,
  });
};

export const useGetFaqs = (salonId: string | null) => {
  return useQuery(GET_FAQS, {
    variables: { salonId },
    skip: !salonId,
  });
};

// Client queries and mutations
export const useSearchClient = ({
  salonId,
  phone,
  countryCode,
}: {
  salonId: string;
  phone?: string;
  countryCode?: string;
}) => {
  return useQuery(SEARCH_CLIENT, {
    variables: { salonId, phone, countryCode },
    skip: !salonId || !phone || !countryCode,
  });
};

export const useCreateClient = () => {
  return useMutation(CREATE_CLIENT);
};

// Appointment queries and mutations
export const useCreateAppointment = () => {
  return useMutation(CREATE_APPOINTMENT);
};

export const useCreateStripeAppointment = () => {
  return useMutation(CREATE_APPOINTMENT_WITH_CLIENT_SECRET);
};

export const useGetAppointmentSummary = (id: string | null) => {
  return useQuery(GET_APPOINTMENT_SUMMARY, {
    variables: { id },
    skip: !id,
  });
};

export const useSalonAvailability = ({
  salonId,
  services,
  startDate,
  endDate,
}: {
  salonId: string;
  services: { serviceId: string; staffIds: string[]; serviceVariantId?: string | null }[];
  startDate?: string;
  endDate?: string;
}) => {
  return useQuery(SALON_AVAILABILITY, {
    fetchPolicy: "network-only",
    variables: { salonId, services, startDate, endDate },
    skip: !salonId || !services?.length || !startDate || !endDate,
  });
};

export const useCreateVoucherPurchase = () => {
  return useMutation(CREATE_VOUCHER_PURCHASE);
};

export const useVoucherPreview = () => {
  return useMutation(VOUCHER_PREVIEW);
};

export const useCreateGiftCardPurchase = () => {
  return useMutation(CREATE_GIFT_CARD_PURCHASE);
};

export const useInitiateGiftCardPurchase = () => {
  return useMutation(INITIATE_GIFT_CARD_PURCHASE);
};

export const useProductPurchasePreview = () => {
  return useMutation(PRODUCT_PURCHASE_PREVIEW, { errorPolicy: "all" });
};

export const useInitiateProductPurchase = () => {
  return useMutation(INITIATE_PRODUCT_PURCHASE, { errorPolicy: "all" });
};

export const useLazyProductOrderSummary = () => {
  return useLazyQuery(PRODUCT_ORDER_SUMMARY);
};

// Service staffs query
export const useGetServiceStaffs = (serviceIds: string[]) => {
  return useQuery(GET_SERVICE_STAFFS, {
    variables: { serviceIds },
    skip: !serviceIds || serviceIds.length === 0,
  });
};

// Reviews and promos are fetched via REST API, not GraphQL
// See: web-frontend/src/api/usePublicV2.ts
